// ModalQueue — single-modal discipline. Any watcher that wants to render a
// blocking overlay calls `useModalSlot(id, wantsToShow, priority)`. The hook
// returns { canShow }; only the highest-priority claimant gets `true`. The
// others wait. When the active one releases, the next pops in. Never two
// at once. This is the bouncer that makes the Sims-reboot direction work.
//
// Priorities (higher = comes up first):
//   95 — year-flow (AnnualReview / BracketMilestone / Legacy)
//   90 — VisionWatcher (dream achieved — precious one-time celebration)
//   80 — ProfitHarvest (Payday — settle-bound, important)
//   75 — ResilienceWatcher (loss-week reframe)
//   70 — ConsequenceWatcher (delayed-consequence recap)
//   60 — KnowledgeCheckWatcher (between-week mentor question)

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

type Ctx = {
  /** Register or update a claim. Idempotent per id. */
  claim: (id: string, priority: number) => void;
  /** Drop a claim by id. Safe to call when not registered. */
  release: (id: string) => void;
  /** The id currently allowed to render (or null when nothing wants to show). */
  activeId: string | null;
};

const ModalCtx = createContext<Ctx>({ claim: () => {}, release: () => {}, activeId: null });

export function ModalQueueProvider({ children }: { children: ReactNode }) {
  // Source of truth is a ref Map so claim/release are stable callbacks. The
  // `activeId` state is what triggers re-renders for consumers.
  const claims = useRef<Map<string, number>>(new Map());
  const [activeId, setActiveId] = useState<string | null>(null);

  const recompute = useCallback(() => {
    let best: { id: string; p: number } | null = null;
    for (const [id, p] of claims.current) {
      if (!best || p > best.p) best = { id, p };
    }
    setActiveId((prev) => (prev === (best?.id ?? null) ? prev : best?.id ?? null));
  }, []);

  const claim = useCallback(
    (id: string, priority: number) => {
      const existing = claims.current.get(id);
      if (existing === priority) return; // no-op, prevents render thrash
      claims.current.set(id, priority);
      recompute();
    },
    [recompute],
  );

  const release = useCallback(
    (id: string) => {
      if (!claims.current.has(id)) return;
      claims.current.delete(id);
      recompute();
    },
    [recompute],
  );

  return <ModalCtx.Provider value={{ claim, release, activeId }}>{children}</ModalCtx.Provider>;
}

/** Watchers call this to ask for the modal stage. canShow is true only when
 *  this id is the current active claimant. Auto-releases on unmount or when
 *  `wantsToShow` flips false. */
export function useModalSlot(id: string, wantsToShow: boolean, priority: number) {
  const { claim, release, activeId } = useContext(ModalCtx);

  useEffect(() => {
    if (wantsToShow) claim(id, priority);
    else release(id);
    return () => release(id);
  }, [wantsToShow, id, priority, claim, release]);

  return { canShow: activeId === id };
}
